import React from "react";
import { Dropdown } from "react-bootstrap";

import { DropdownButton, MenuList, bytesToHumanReadableSize } from "../../../libs";

import * as Styled from "./Sidemenu.styled";

import { IUploadType } from "../../upload-modal/types";

import { useAuth, useGlobalState } from "../../../behavioral";
import { paymentService, userService } from "../../../services";

interface ISideMenuProps {
  onClickUploadVideo: (uploadType: IUploadType) => void;
}

export const DashboardSideMenu: React.FC<ISideMenuProps> = ({ onClickUploadVideo }) => {
  const uploadVideoHandler = React.useCallback(() => {
    onClickUploadVideo(IUploadType.FILM_FESTIVAL_VIDEO);
  }, [onClickUploadVideo]);

  const uploadGeneralVideoHandler = React.useCallback(() => {
    onClickUploadVideo(IUploadType.GENERAL_VIDEO);
  }, [onClickUploadVideo]);

  const liveStreamHandler = React.useCallback(() => {}, []);

  const { state, dispatch, actions } = useGlobalState();

  const auth = useAuth();
  const [actingUserEmail, setActingUserEmail] = React.useState<string>("");

  React.useEffect(() => {
    const fetchUser = async () => {
      if (localStorage.getItem("actAsUserId")) {
        try {
          const user = await userService.getCurrentUser();
          setActingUserEmail(user.email);
        } catch (error) {
          console.error("Failed to fetch user:", error);
        }
      }
    };
    fetchUser();
  }, []);

  React.useEffect(() => {
    const refreshSubscriptionStatus = async () => {
      if (!state.currentUser?.subscriptionInfo?.subscriptionId) return;
      try {
        const result = await paymentService.getMySubscription();
        dispatch({
          type: actions.SET_USER,
          payload: {
            ...state.currentUser,
            subscriptionInfo: result.subscriptionInfo || state.currentUser.subscriptionInfo,
          },
        });
      } catch (error) {
        console.error("Failed to refresh subscription status", error);
      }
    };
    refreshSubscriptionStatus();
    // Refresh once when the sidebar is mounted; Stripe webhooks keep the stored state current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const menuList = [
    {
      itemText: "Dashboard",
      linkPath: "/dashboard",
    },
    {
      itemText: "Video-On-Demand",
      linkPath: "/vod-uploads",
    },
    {
      itemText: "Playlists",
      linkPath: "/playlists",
    },
    {
      itemText: "Live Stream",
      children: [
        {
          itemText: "Channels",
          linkPath: "/live-channels",
        },
        {
          itemText: "Recordings",
          linkPath: "/recordings",
        },
      ],
    },
    {
      itemText: "Ad Server",
      linkPath: "/ad-server",
    },
    {
      itemText: "Manage Payments",
      children: [
        {
          itemText: "My Plan",
          linkPath: "/manage-payments/my-plan",
        },
        {
          itemText: "Payments and Plans",
          linkPath: "/manage-payments/payment-plans",
        },
        {
          itemText: "Access Tokens",
          linkPath: "/manage-payments/manage-access-tokens",
        },
        {
          itemText: "Referral Programs",
          linkPath: "/manage-payments/referral-programs",
        },
        {
          itemText: "Coupon Codes",
          linkPath: "/manage-payments/coupon-codes",
        },
      ],
    },
    {
      itemText: "Manage Storage",
      linkPath: "/manage-storage",
    },
    {
      itemText: "Files",
      linkPath: "/files",
    },
    {
      itemText: "Manage Libraries",
      children: [
        {
          itemText: "Manage Controls",
          linkPath: "/manage-libraries/manage-controls",
        },
        // {
        //   itemText: "Manage Templates",
        //   linkPath: "/manage-libraries/manage-templates",
        // },
        {
          itemText: "Manage Profiles",
          linkPath: "/manage-libraries/manage-profiles",
        },
        {
          itemText: "Library Outlets",
          linkPath: "/manage-libraries/library-outlets",
        },
        {
          itemText: "Embed Code",
          linkPath: "/manage-libraries/embed-code",
        },
      ],
    },
    {
      itemText: "Support",
      linkPath: "/support-ticketing",
    },
  ];

  if (state.isAdmin) {
    menuList.push({
      itemText: "Admin",
      children: [
        {
          itemText: "Manage Users",
          linkPath: "/admin/manage-users",
        },
        {
          itemText: "Manage Packages",
          linkPath: "/admin/manage-packages",
        },
        {
          itemText: "Manage Public Plans",
          linkPath: "/admin/manage-public-plans",
        },
        {
          itemText: "Contact Inquiries",
          linkPath: "/admin/contact-inquiries",
        },
      ],
    });
  }

  const usedPercentage = React.useMemo(() => {
    return state.storage.limit > 0 ? (state.storage.used / state.storage.limit) * 100 : 0;
  }, [state.storage.limit, state.storage.used]);

  return (
    <>
      <Styled.SideMenuUploadCta>
        <DropdownButton>
          <Dropdown.Item onClick={uploadGeneralVideoHandler}>Upload a General Video</Dropdown.Item>
          <Dropdown.Item onClick={uploadVideoHandler}>Upload a Film Festival Video</Dropdown.Item>
          <Dropdown.Item onClick={liveStreamHandler}>Start Live Streaming</Dropdown.Item>
        </DropdownButton>
      </Styled.SideMenuUploadCta>
      <Styled.SideMenuListWrapper>
        <MenuList menuList={menuList} />
      </Styled.SideMenuListWrapper>
      {localStorage.getItem("actAsUserId") && (
        <Styled.AdminModeAlert>
          <Styled.AdminModeHeader>
            <span>⚠️</span>
            <Styled.AdminModeText>
              Admin: {auth.user?.attributes?.email}
              <br /> Acting as: {actingUserEmail || "..."}
            </Styled.AdminModeText>
          </Styled.AdminModeHeader>
          <Styled.AdminModeButton
            onClick={() => {
              localStorage.removeItem("actAsUserId");
              window.location.reload();
            }}
          >
            Exit Acting
          </Styled.AdminModeButton>
        </Styled.AdminModeAlert>
      )}
      {["PAYMENT_FAILED", "ACTION_REQUIRED"].includes(
        state.currentUser?.subscriptionInfo?.paymentStatus || ""
      ) &&
        !state.currentUser?.subscriptionInfo?.pendingManualBilling &&
        state.currentUser?.subscriptionInfo?.billingMode !== "MANUAL" && (
          <Styled.PaymentIssueAlert>
            <Styled.PaymentIssueTitle>Payment issue</Styled.PaymentIssueTitle>
            <Styled.PaymentIssueText>
              Your latest Cemboo subscription payment did not go through.
            </Styled.PaymentIssueText>
            <Styled.PaymentIssueLink href="/manage-payments/my-plan">
              Review My Plan <i className="fas fa-arrow-right"></i>
            </Styled.PaymentIssueLink>
          </Styled.PaymentIssueAlert>
        )}
      <Styled.SideMenuUpgradeCard>
        {usedPercentage > 70 && <Styled.UpgradeHeader>Need more from Cemboo?</Styled.UpgradeHeader>}
        <Styled.UsageWrapper>
          <Styled.UsageProgressBar now={usedPercentage} variant="success" />
          <Styled.UsageText>
            {bytesToHumanReadableSize(state.storage.used)} of{" "}
            {bytesToHumanReadableSize(state.storage.limit)} used
          </Styled.UsageText>
        </Styled.UsageWrapper>
        <Styled.UpgradeCtaLink href="/manage-payments/my-plan">
          Upgrade to next level <i className="fas fa-arrow-right"></i>
        </Styled.UpgradeCtaLink>
      </Styled.SideMenuUpgradeCard>
    </>
  );
};
